import { useGetAdminMessageReports } from '@/api/generated/admin/admin'
import { useUser } from '@/hooks/use-user'

/**
 * Count of message reports still awaiting a moderator, for the "there's work
 * here" badges. Reporting a message only inserts a queue row — nothing pushes
 * it to anyone — so without a visible count the queue is only found by
 * remembering to look.
 *
 * Reuses the queue endpoint rather than adding a count-only one: the response
 * already carries `pendingCount`, and the queue is small. Returns 0 for
 * non-admins (and never fires the request for them).
 */
export function usePendingMessageReports(): number {
  const { user } = useUser()
  const canSee = !!(user?.isAdmin || user?.isSuperUser)
  const { data } = useGetAdminMessageReports(
    { includeReviewed: false },
    {
      query: {
        enabled: canSee,
        // A moderation queue is not live data — refetching on every window
        // focus would hammer an admin-only endpoint for a number that changes
        // a few times a week.
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    },
  )
  return data?.pendingCount ?? 0
}
